import { TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { useComponentVisible } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import i18n from 'locales/i18n';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetStatementRowsInfinite } from 'services/statements';
import {
  IStatementRow,
  IStatementRowsFilterParams,
} from 'services/statements/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';

const Rows = () => {
  const { id = '', accountId = '' } = useParams();
  const { t } = useTranslation();

  const [filters, setFilters] = useState<IStatementRowsFilterParams>({});
  const rows = useGetStatementRowsInfinite(id, accountId, filters);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const tableData = useInfiniteTable<IStatementRow>(rows, { selectable: false });

  const onCopy = (value?: string | null) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(t('toast-message.copied-what', { what: value }), {
      id: value,
    });
  };

  return (
    <div className='w-full h-full gap-2 flex flex-col'>
      <TopBar
        title={t('statements.tabs.rows')}
        customBackItem={<div />}
        topbarContent={
          <QueryFilter.ButtonOpener
            i18nextPrefix='statements'
            onClick={handleComponentVisible}
            variant={BUTTON_VARIANT.default}
            isClear
          />
        }>
        <QueryFilter<IStatementRowsFilterParams>
          i18nextPrefix='statements'
          initialValues={filters}>
          <>
            <QueryFilter.Form<IStatementRowsFilterParams>
              ref={ref}
              isOpen={isComponentVisible}
              onDebouncedFormChange={values => setFilters(values)}>
              <QueryFilter.InputsGrid>
                <FormInput.Text name='ean' label={t('input.label.ean')} />
                <FormInput.Text name='isrc' label={t('input.label.isrc')} />
                <FormInput.Text
                  name='channelId'
                  label={t('input.label.channelId')}
                />
                <FormInput.Text
                  name='videoId'
                  label={t('input.label.videoId')}
                />
                <FormInput.Text
                  name='assetId'
                  label={t('input.label.assetId')}
                />
              </QueryFilter.InputsGrid>
            </QueryFilter.Form>
            <QueryFilter.Labels
              isFormOpen={isComponentVisible}
              customLabel={data =>
                data.value && `${data.value}`.trim() !== ''
                  ? `${data.value}`
                  : undefined
              }
            />
          </>
        </QueryFilter>
      </TopBar>
      <Table
        id='AdminStatementRows'
        size='compact'
        {...tableData}
        columns={[
          {
            label: t('input.label.identifier'),
            renderCell: (item: IStatementRow) => (
              <TableItem>
                {!!item.ean?.length && (
                  <div
                    className='cursor-pointer'
                    onClick={() => onCopy(item.ean)}>
                    <TableItem.Text text={item.ean} isSupplementary />
                  </div>
                )}
                {!!item.isrc?.length && (
                  <div
                    className='cursor-pointer'
                    onClick={() => onCopy(item.isrc)}>
                    <TableItem.Text text={item.isrc} isSupplementary />
                  </div>
                )}
                {!!item.channelId?.length && (
                  <div
                    className='cursor-pointer'
                    onClick={() => onCopy(item.channelId)}>
                    <TableItem.Text text={item.channelId} isSupplementary />
                  </div>
                )}
                {!!item.videoId?.length && (
                  <div
                    className='cursor-pointer'
                    onClick={() => onCopy(item.videoId)}>
                    <TableItem.Text text={item.videoId} isSupplementary />
                  </div>
                )}
                {!!item.assetId?.length && (
                  <div
                    className='cursor-pointer'
                    onClick={() => onCopy(item.assetId)}>
                    <TableItem.Text text={item.assetId} isSupplementary />
                  </div>
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.country'),
            renderCell: (item: IStatementRow) => (
              <TableItem>
                <TableItem.Text
                  text={
                    i18n.language === 'en'
                      ? item.country?.englishName
                      : item.country?.polishName
                  }
                />
                {item.countryCode && (
                  <TableItem.Text text={item.countryCode} isSupplementary />
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.totalValue'),
            renderCell: (item: IStatementRow) => (
              <TableItem>
                <TableItem.Text
                  text={formatCurrency(item.totalValue, {
                    fractionDigits: 4,
                  })}
                />
                <TableItem.Text
                  text={`${formatCurrency(item.unitPrice, {
                    fractionDigits: 4,
                  })} x ${item.quantity}`}
                  isSupplementary
                />
              </TableItem>
            ),
          },
        ]}
      />
    </div>
  );
};

export default Rows;
